import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import { AdminNav } from '@/app/AdminNav';
import { useStaffAuth } from '@/features/auth/useStaffAuth';
import { AppearanceControl } from '@/features/theme/AppearanceControl';

/**
 * The signed-in admin frame — a 230 px LEFT RAIL since slice 17e.
 *
 * WHY IT CHANGED. It shipped as a `border-b` top bar. **All NINE admin
 * artboards** (`1s 1t 1u 1v 3d 5f 5g 6i 6j`) specify, in the per-element
 * `### Layout skeleton` produced by 24i's extractor:
 *
 *   frame div  display:flex  flex-direction:row  width:1440px  height:880px
 *     div  display:flex  flex-direction:column  width:230px  flex:none
 *     div  display:flex  flex-direction:column  min-width:0  flex:1
 *
 * Zero exceptions. Mahad found the divergence BY EYE, which is the argument
 * that produced the extractor: an audit had reported "zero real gaps" on the
 * admin screens for a dimension it could not measure.
 *
 * MEASURED, in a real browser at the canvas's own 1440x880 viewport:
 *   before   aside ABSENT            main.x=144  main.w=1152  nav horizontal
 *   after    aside x=0 w=230 h=880   main.x=230  main.w=1210  nav vertical
 * 1152 is `max-w-6xl` (72rem) exactly and 144 is (1440-1152)/2 — the cap was
 * costing 144 px of dead margin on each side at the canvas's own width.
 *
 * 🔴 THE RAIL'S NUMBERS COME FROM THE CANVAS, NOT FROM `web/AppShell.tsx`.
 * The web shell already ships this structure and is the right thing to port the
 * STRUCTURE from — but its values are the customer app's. Rail width (230px)
 * and the content column's `min-width:0; flex:1` come from the SKELETON, which
 * is per-element, so those two are exact. Type and padding come from
 * intersecting all nine token inventories — see `AdminNav`, where three of four
 * values differ from what the web shell ships.
 *
 * ⚠️ AND ITS TOKENS DO NOT EXIST HERE. `web/AppShell.tsx` styles this rail with
 * `bg-raad-deep` and `text-raad-dim`. **46 `--color-raad-*` declarations exist
 * in `web/src/styles.css` and not in `admin/src/styles.css`** (57 vs 11; 26
 * distinct base names once numbered scales like `raad-line-10..40` are folded),
 * those two among them. Copying the class strings would ship classes that
 * resolve to NOTHING and render unstyled — the `text-raad-danger` defect that
 * left error text colourless in the client's app for a day in 24e, and what
 * `ci/check-tailwind-tokens.mjs` exists to catch. Only tokens declared in
 * `admin/src/styles.css` appear below.
 *
 * The canvas draws the rail DARKER than the content (`#0d0f16`/`#0a0c12`).
 * Admin has no darker-than-`raad-bg` token, and inventing one is a colour
 * decision belonging with the admin dark tokens — deliberately NOT taken here.
 * `bg-raad-panel` + a border gives a distinct surface from tokens that exist.
 * Recorded so the next reader knows it is a deferral, not an oversight.
 *
 * ONE ELEMENT, REORIENTED — the nav, the brand and the account block exist
 * ONCE. The first attempt rendered a rail AND a mobile bar and let CSS hide one,
 * which put two same-named navs and two "Sign out" buttons in the DOM —
 * invisible to CSS-less consumers like jsdom and screen readers, and caught by
 * three existing tests going red.
 *
 * SLICE 84b — MOBILE IS A SLIDE-IN DRAWER, NOT A WRAPPING TOP BAR. Below `lg`
 * the pre-84b aside wrapped all 6–11 nav pills + the account block across the top
 * and ate ~200px of height before any content. Now this SAME `<aside>` is an
 * off-canvas left drawer (`fixed`, `-translate-x-full`, slid in on open); from
 * `lg` it is the unchanged 230px static rail. A compact top bar (`lg:hidden`)
 * carries ONLY the brand + a hamburger — the hamburger is a UNIQUE control, not a
 * second copy of anything in the drawer, and a cosmetic brand span is not a
 * nav/landmark/button, so the single-nav invariant still holds: nav, account and
 * "Sign out" each appear exactly once, in the drawer. The drawer being `fixed`
 * keeps it out of document flow, so off-canvas it adds no horizontal scroll.
 *
 * NAV STAYS REACHABLE ON A PHONE. Slice 09 shipped a desktop sidebar that was
 * `display:none` at 360px, so only desktop users could see it was wrong. Here
 * the same element carries the nav at every width, and the CI gate's A-5
 * asserts all four links are laid out at 390px.
 *
 * Indigo throughout. Green belongs to the customer dashboard, and the accent
 * may be the only thing telling a staff member whose data is on screen once
 * impersonation lands.
 */

/** First letters of up to two words — pure, so it is trivially testable. */
function initials(label: string): string {
  return label
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const { t } = useTranslation();
  const { state, logout } = useStaffAuth();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const drawerRef = useRef<HTMLElement>(null);

  // /live is a full-screen map on a phone, so `main` drops its padding for it
  // below `lg` (the shell mechanism that REPLACES GlobalLive's old `-mx-5 -my-6`
  // negative-margin hack). At `lg` the padding returns to exactly today's values,
  // so desktop /live is unchanged. Matched to GlobalLive's own phone breakpoint
  // (max-width:767 → md). Only /live: /zone-library is a content page (list +
  // publish form + a bordered draw map) that KEEPS its phone gutter.
  const fullBleed = location.pathname === '/live';

  // Close the drawer whenever the route changes (a nav selection navigated).
  useEffect(() => {
    setMenuOpen(false);
  }, [location.pathname]);

  // While the drawer is open: Escape closes it, the body scroll is locked so the
  // page behind does not scroll under the overlay, and focus moves into the
  // drawer. Closing restores focus to the hamburger (handled in `closeMenu`).
  useEffect(() => {
    if (!menuOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMenuOpen(false);
        hamburgerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    drawerRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [menuOpen]);

  const closeMenu = () => {
    setMenuOpen(false);
    hamburgerRef.current?.focus();
  };

  // 🔴 THIS IS A TYPE NARROWING, NOT A GUARD, AND THE DISTINCTION IS THE POINT.
  // `RequireStaff` renders `<Login />` INSTEAD of this component when the
  // session is anonymous or still loading (RequireStaff.tsx:23-31), so the
  // status here is always 'authenticated' and this branch does not execute.
  //
  // An earlier revision of this file carried a whole signed-out `<header>` with
  // a comment explaining the login chrome. That branch was UNREACHABLE, and a
  // comment describing behaviour that cannot happen is the exact defect this
  // codebase rules a defect. Proven by measuring the signed-out app in a real
  // browser: no <header>, no <aside>, <main> filling the viewport — the Login
  // screen, rendered in place of this shell. Do not add signed-out chrome here;
  // it belongs in `Login`, which is the component that actually renders.
  if (state.status !== 'authenticated') return null;
  const user = state.user;
  const who = user.name ?? user.phone;

  return (
    <div className="min-h-dvh overflow-x-hidden bg-raad-bg text-raad-text lg:flex lg:overflow-x-visible">
      {/* COMPACT TOP BAR — phone only (`lg:hidden`). Brand + hamburger, nothing
          else. `sticky` so the menu is always reachable; `--raad-safe-top` keeps
          it clear of the status bar / notch. */}
      <header className="sticky top-0 z-30 flex items-center gap-3 border-b border-raad-border bg-raad-panel px-4 pb-2.5 pt-[calc(0.625rem+var(--raad-safe-top))] lg:hidden">
        <button
          ref={hamburgerRef}
          type="button"
          onClick={() => setMenuOpen((o) => !o)}
          aria-expanded={menuOpen}
          aria-controls="admin-drawer"
          aria-label={menuOpen ? t('shell.closeMenu') : t('shell.openMenu')}
          className="-ms-1 flex size-11 flex-none items-center justify-center rounded-lg border border-raad-border text-raad-text transition-colors hover:bg-raad-primary/10"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
            <path
              d="M3 5h14M3 10h14M3 15h14"
              stroke="currentColor"
              strokeWidth="1.8"
              strokeLinecap="round"
            />
          </svg>
        </button>
        <div className="flex items-center gap-2.5">
          <span className="size-2.5 flex-none rounded-full bg-raad-primary" />
          <span className="font-display text-[17px] font-bold tracking-tight">{t('brand')}</span>
        </div>
      </header>

      {/* BACKDROP — phone only, only while open. Click closes. */}
      {menuOpen ? (
        <div
          className="fixed inset-0 z-40 bg-black/50 lg:hidden"
          aria-hidden="true"
          onClick={closeMenu}
        />
      ) : null}

      {/* ONE ELEMENT — the off-canvas drawer below `lg`, the 230px static rail
          from `lg`. The `lg:` classes are the pre-84b rail VERBATIM, so desktop
          layout is byte-for-byte unchanged; the base (phone) classes make it a
          fixed slide-in panel. Padding 18px/12px/16px is the compound spacing
          value present on all nine admin artboards. */}
      <aside
        ref={drawerRef}
        id="admin-drawer"
        tabIndex={-1}
        className={[
          'fixed inset-y-0 left-0 z-50 flex w-[280px] flex-col overflow-y-auto border-r border-raad-border bg-raad-panel outline-none',
          'px-3 pb-[calc(1rem+var(--raad-safe-bottom))] pl-[calc(0.75rem+var(--raad-safe-left))] pt-[calc(18px+var(--raad-safe-top))]',
          'transition-transform duration-200 ease-out',
          menuOpen ? 'translate-x-0' : '-translate-x-full',
          'lg:static lg:z-auto lg:w-[230px] lg:translate-x-0 lg:flex-none lg:flex-col lg:items-stretch lg:gap-0 lg:border-b-0 lg:border-r lg:px-3 lg:pb-4 lg:pl-3 lg:pt-[18px]',
        ].join(' ')}
      >
        <div className="flex items-center gap-2.5 lg:px-2">
          <span className="size-2.5 flex-none rounded-full bg-raad-primary" />
          <span className="font-display text-[17px] font-bold tracking-tight">{t('brand')}</span>
        </div>

        <div className="mt-5 flex-1 lg:mt-5 lg:flex-1">
          <AdminNav onNavigate={closeMenu} />
        </div>

        {/* Slice 23 — the per-device theme preference. No Settings screen exists
            in admin (see AppearanceControl), so it lives beside the account
            block, above the account divider. */}
        <AppearanceControl />

        <div className="mt-2 flex items-center gap-2.5 border-t border-raad-border px-2 pt-3 lg:ml-0 lg:mt-2 lg:border-t lg:border-raad-border lg:px-2 lg:pt-3">
          <span className="flex size-8 flex-none items-center justify-center rounded-full bg-raad-primary font-display text-xs font-bold text-raad-onprimary">
            {initials(who)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-[12px] font-semibold">{who}</div>
            <div className="text-[10.5px] text-raad-muted">{t(`shell.role.${user.role}`)}</div>
          </div>
          <button
            type="button"
            onClick={() => void logout()}
            className="ms-auto flex min-h-11 flex-none items-center rounded-lg border border-raad-border px-3 text-[12px] font-semibold text-raad-muted transition-colors hover:text-raad-text lg:ms-auto lg:min-h-9"
          >
            {t('shell.signOut')}
          </button>
        </div>
      </aside>

      {/* CONTENT COLUMN — `min-width:0; flex:1` from the skeleton. `min-w-0` is
          load-bearing: without it a wide table refuses to shrink and pushes the
          whole row past the viewport. `mx-auto max-w-6xl` removed (item 9). For a
          full-bleed map route the padding is dropped below `lg` and returns to
          today's values at `lg` (desktop unchanged). */}
      <main
        className={
          fullBleed
            ? 'min-w-0 flex-1 p-0 md:px-6 md:py-6 lg:py-8'
            : 'min-w-0 flex-1 px-5 py-6 sm:px-6 lg:py-8'
        }
      >
        {children}
      </main>
    </div>
  );
}
